import type { TranslationSchema } from '../pt-BR';
import type { DeepPartialStrings } from '../types';

export const projections = {
    layout: {
        indexTitle: 'Projections',
        shortTermTitle: 'Short Term',
        mediumTermTitle: 'Medium Term',
        whatIfTitle: 'Simulator',
    },
    hub: {
        projectedBalance: 'Projected balance',
        projectedAvailable: 'Projected available',
        reservedNote: '{{amount}} set aside in pockets is not included',
        shortTermTitle: 'Short Term',
        shortTermSubtitle: 'Day-by-day projection (30-90 days)',
        mediumTermTitle: 'Medium Term',
        mediumTermSubtitle: 'Scenarios for 3-12 months',
        whatIfTitle: 'What-If Simulator',
        whatIfSubtitle: 'What if I add/remove an expense?',
    },
    shortTerm: {
        period30: '30 days',
        period60: '60 days',
        period90: '90 days',
        today: 'today',
        kickerBreach: 'Your cash will not last the {{days}} days',
        kickerAlready: 'Even before the next due dates',
        kickerSafe: 'With what is already scheduled',
        headlineBreach: 'Runs out on {{date}}',
        headlineAlready: 'Already in the red',
        headlineSafe: 'Lasts all {{days}} days',
        detailShortfall: 'Short by',
        detailSlack: 'Tightest point',
        detailRecovers: 'back in the black on {{date}}',
        detailNoRecovery: 'and does not recover within the horizon',
        detailSlackDate: 'on {{date}}',
        curveRecovery: 'back in the black on {{date}}',
        slackLabel: 'Tightest point',
        endsOn: 'Ends on {{date}}',
        actionSimulate: 'Simulate the {{amount}}',
        actionPostpone: 'Postpone this expense',
        eventsTitle: 'What happens until {{date}}',
        runningBalance: 'balance after',
        breachHere: 'this is where it breaks',
        source: {
            recurring: 'recurring',
            invoice: 'statement',
        },
        underwaterDays_one: 'below zero for {{count}} day',
        underwaterDays_other: 'below zero for {{count}} days',
        underwaterOpen: 'stays below zero until the end of the horizon',
        showAll_one: 'Show {{count}} more event',
        showAll_other: 'Show {{count}} more events',
        showLess: 'Show less',
        note: "Adds to today's balance everything already scheduled in the period: recurrences, open statements and pending entries from the included accounts.",
        emptyTitle: 'Not enough data to project yet',
        emptySubtitle: 'Once there are enough accounts, balance and financial events, this curve will show the short-term horizon.',
        emptyEventsTitle: 'No notable events in this horizon',
        emptyEventsSubtitle: 'If due dates, recurring items or relevant activity come up in the next few days, they will be listed here.',
    },
    mediumTerm: {
        period3: '3 months',
        period6: '6 months',
        period12: '12 months',
        kicker: 'Keeping the current pace',
        headlineTurns: '{{month}} closes in the red',
        headlineStaysRed: 'Stays in the red through {{month}}',
        headlineHolds: 'Stays in the black for {{months}} months',
        detailIn: 'In {{month}}:',
        detailRange: 'between {{low}} and {{high}}',
        legendLine: 'projection',
        legendMargin: 'margin of error (±{{amount}})',
        committedTitle: 'How much of it is already committed',
        legendCommitted: 'committed',
        legendEstimated: 'estimated',
        committedShare: '{{percent}} committed',
        heaviestPairTitle: '{{first}} and {{second}} weigh together',
        heaviestPairSubtitle: '{{amount}} in commitments already recorded',
        methodologyTitle: 'How we calculate it',
        emptyTitle: 'Not enough data for a trend yet',
        emptySubtitle: 'Once there is enough history of inflows, outflows and balance, the projection and its margin of error will appear here.',
        chartFootnote: 'Adds up the commitments already recorded (recurrences, invoices and scheduled items) and estimates the rest from the last 3 months. The band varies ±10% on the estimated part only — what is already contracted does not swing.',
    },
    whatIf: {
        typeAddExpense: 'Add expense',
        typeRemoveExpense: 'Cut expense',
        typeIncomeChange: 'Change income',
        typeShortAdd: 'Add',
        typeShortCut: 'Cut',
        typeShortIncome: 'Change income',
        actionAdd: 'Adding an expense',
        actionAddNamed: 'Adding {{name}}',
        actionCut: 'Cutting an expense',
        actionCutNamed: 'Cutting {{name}}',
        actionIncome: 'Changing income',
        actionIncomeNamed: 'Changing income · {{name}}',
        perWeek: '{{amount}}/week',
        perMonth: '{{amount}}/mo',
        perYear: '{{amount}}/yr',
        frequencyWeekly: 'Weekly',
        frequencyMonthly: 'Monthly',
        frequencyAnnual: 'Annual',
        horizon6: '6 months',
        horizon12: '12 months',
        horizon24: '24 months',
        scenarioNameAdd: 'Add {{name}}',
        scenarioNameCut: 'Cut {{name}}',
        scenarioNameIncome: 'Income · {{name}}',
        scenarioFallback: 'Scenario',
        headline: 'You end {{month}} with {{amount}}',
        detailAbove: 'more than your path today',
        detailBelow: 'less than your path today',
        emptyKicker: 'No scenario yet',
        emptyHeadline: 'What if you cut an expense?',
        emptyDetail: 'Pick an amount below to see how your path changes.',
        setupTitle: 'What to simulate',
        setupEmptyValue: 'Choose',
        applySetup: 'See result',
        importSectionTitle: 'YOUR RECURRING EXPENSES',
        amountLabel: 'Amount',
        clearSelection: 'Clear selection',
        selectedExpenses_one: '{{count}} expense',
        selectedExpenses_other: '{{count}} expenses',
        importEmpty: 'No active recurring expense or subscription to pick.',
        save: 'Save',
        scenarioSaved: 'Scenario saved',
        verdictClearsRed_one: 'Clears the red: today {{first}} would close negative — with this scenario, no month does.',
        verdictClearsRed_other: 'Clears the red: today you would close {{count}} negative months between {{first}} and {{last}} — with this scenario, none.',
        verdictReducesRed: 'Eases it, but does not fix it: from {{from}} to {{to}} months closing in the red.',
        verdictCausesRed_one: 'Pushes you into the red: {{month}} starts closing negative.',
        verdictCausesRed_other: 'Pushes you into the red: {{count}} months start closing negative, from {{month}} on.',
        verdictWorsensRed: 'Tightens it further: from {{from}} to {{to}} months closing in the red.',
        verdictStaysRed_one: 'Nothing essential changes: {{count}} month still closes in the red.',
        verdictStaysRed_other: 'Nothing essential changes: {{count}} months still close in the red.',
        verdictStaysBlue: 'No month closes in the red over the next {{months}} months — neither today nor with this scenario.',
        chipGoals: 'covers {{amount}}/mo of your goals',
        chipBudgets_one: '{{count}} budget at risk',
        chipBudgets_other: '{{count}} budgets at risk',
        chipInvoices: '{{amount}} in open invoices',
        chartBaselineBreach: 'today: red in {{month}}',
        legendScenario: 'scenario',
        legendToday: 'today',
        deltaTitle: 'Month-by-month difference',
        deltaFootnote: 'Builds up {{monthly}}/mo · {{total}} by the end',
        showNumbers: 'See numbers',
        hideNumbers: 'Hide',
        colMonth: 'MONTH',
        colCurrent: 'TODAY',
        colScenario: 'SCENARIO',
        savedTitle: 'Saved scenarios',
        savedHint: 'tap to use · swipe to delete',
        useScenarioA11y: 'Use scenario',
        compare: 'Compare',
        stopComparing: 'Stop comparing',
        suggestionsTitle: 'Start from one of your expenses',
    },
} satisfies DeepPartialStrings<TranslationSchema['projections']>;
